// 添加 Mapbox GL JS 类型定义
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './MapboxMapView.module.css';
import { useMapBoxPageLoadTrack } from '../../../../graphql/mutations/mapBoxTrackLog';

declare global {
    interface Window {
        mapboxgl: any;
    }
}

// 注意：在 Mapbox 控制台设置 API Key 的域名白名单，即使 key 泄露，别人也无法在非白名单域名下使用
const DEFAULT_MAPBOX_API_KEY = "pk.eyJ1IjoibGl4aWFvaHVpdGl0YSIsImEiOiJjbWNsc2J3aTcwZDkyMmtwdGxiZW95YWt6In0.qoTeK6P5iQ8Mq62KdImwBQ";

// 标记点属性接口
interface MarkerProperties {
    id: string;
    number?: number|String;
    title?: string;
    description?: string;
    type?: string;
    status?: string;
    color?: string;
}

interface MapMarker {
    position: {
        lat: number;
        lng: number;
    };
    properties: MarkerProperties;
}

interface MapboxDetailViewProps {
    stopNumber: number|String;
    address: string;
    longitude?: number;
    latitude?: number;
    markerProperties?: Partial<MarkerProperties>; // 都设置为可选
    onCoordinatesReady?: (latitude: number, longitude: number) => void; // 当地理编码完成后回调
    pageMarker?: string; // 页面标记，用于地图加载日志跟踪
}

// 默认地图配置，纽约市曼哈顿的中心位置，可以进一步修改成用户浏览器位置
const DEFAULT_MAP_CONFIG = {
    center: {
        lat: 40.7128,
        lng: -74.0060
    },
    zoom: 15 // 默认缩放级别，根据需要进行调整
};

// 加载 Mapbox GL JS
const loadMapboxScript = (apiKey: string): Promise<any> => {
    // 动态加载CSS
    const loadCSS = () => {
        const cssId = 'mapbox-gl-css';
        if (!document.getElementById(cssId)) {
            const link = document.createElement('link');
            link.id = cssId;
            link.rel = 'stylesheet';
            link.type = 'text/css';
            link.href = 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css';
            document.head.appendChild(link);
        }
    };

    // 检查是否已经加载完成
    const checkMapbox = () => {
        if (window.mapboxgl) {
            console.log('Mapbox GL JS is ready');
            return true;
        }
        return false;
    };

    return new Promise((resolve, reject) => {
        // 如果已经加载好，直接 resolve
        if (checkMapbox()) {
            resolve(window.mapboxgl);
            return;
        }

        // 加载CSS
        loadCSS();

        // 加载JS
        const scriptId = 'mapbox-gl-js';
        if (document.getElementById(scriptId)) {
            // 脚本已存在，等待加载完成
            const checkInterval = setInterval(() => {
                if (checkMapbox()) {
                    clearInterval(checkInterval);
                    resolve(window.mapboxgl);
                }
            }, 100);
            return;
        }

        const script = document.createElement('script');
        script.id = scriptId;
        script.type = 'text/javascript';
        script.src = 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.js';
        script.async = true;
        script.defer = true;
        script.onload = () => {
            const checkInterval = setInterval(() => {
                if (checkMapbox()) {
                    clearInterval(checkInterval);
                    resolve(window.mapboxgl);
                }
            }, 100);
        };
        script.onerror = (error) => {
            console.error('Failed to load Mapbox GL JS:', error);
            reject(new Error('Failed to load Mapbox GL JS'));
        };
        document.head.appendChild(script);
    });
};

// Mapbox GL JS 服务
const mapboxService = {
    // 初始化地图
    initializeMap: async (container: HTMLElement, apiKey: string, initialAddress?: string, providedLng?: number, providedLat?: number) => {
        console.log('Initializing map...');
        try {
            await loadMapboxScript(apiKey);
            console.log('Mapbox GL loaded');

            window.mapboxgl.accessToken = apiKey;

            // 如果有初始地址或有效的经纬度，先获取位置
            let initialCenter = DEFAULT_MAP_CONFIG.center;
            if (initialAddress || (providedLng !== undefined && providedLng !== null && 
                                   providedLat !== undefined && providedLat !== null)) {
                try {
                    const position = await mapboxService.geocode(initialAddress || '', apiKey, providedLng, providedLat);
                    if (position) {
                        initialCenter = position;
                    }
                } catch (error) {
                    console.warn('Failed to get initial position:', error);
                }
            }

            const map = new window.mapboxgl.Map({
                container: container,
                style: 'mapbox://styles/mapbox/streets-v11',
                center: [initialCenter.lng, initialCenter.lat], // Mapbox 使用 [lng, lat] 顺序
                zoom: DEFAULT_MAP_CONFIG.zoom
            });

            console.log('Map instance created');

            // 添加导航控制
            map.addControl(new window.mapboxgl.NavigationControl(), 'bottom-right');
            console.log('Map controls added');

            // 全局监听缩放和数字隐藏
            map.on('zoom', () => {
                const zoom = map.getZoom();
                const scale = Math.max(0.5, Math.min(zoom / 14, 2));
                document.querySelectorAll('.custom-map-pin').forEach(pin => {
                    (pin as HTMLElement).style.transform = `scale(${scale})`;
                    (pin as HTMLElement).style.transformOrigin = 'bottom center';
                });
                document.querySelectorAll('.custom-map-pin-number').forEach(numberSpan => {
                    (numberSpan as HTMLElement).style.display = zoom < 13 ? 'none' : 'block';
                });
            });

            return map;
        } catch (error) {
            console.error('Error initializing map:', error);
            throw error;
        }
    },

    // 地理编码服务 - 支持直接使用经纬度或通过地址获取
    geocode: async (address: string, apiKey: string, providedLng?: number, providedLat?: number) => {
        // 如果已经提供了有效的经纬度，直接返回
        if (providedLng !== undefined && providedLng !== null && 
            providedLat !== undefined && providedLat !== null) {
            console.log('Using provided coordinates:', { lat: providedLat, lng: providedLng });
            return { lat: providedLat, lng: providedLng };
        }

        // 否则使用地理编码服务
        try {
            const response = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${apiKey}&limit=1`
            );

            const data = await response.json();

            if (data.features?.[0]?.center) {
                const [lng, lat] = data.features[0].center;
                console.log('Geocoded address to coordinates:', { lat, lng });
                return { lat, lng };
            }
            console.warn('No location found for address:', address);
            return null;
        } catch (error) {
            console.error('Geocoding error:', error);
            return null;
        }
    },

    // 添加标记点
    updateMarker: (map: any, marker: MapMarker, currentMarkerRef: any) => {
        // 移除旧的标记
        if (currentMarkerRef.current) {
            currentMarkerRef.current.remove();
        }

        const { position, properties } = marker;
        // 极简黑底白字风格 pin
        const markerElement = document.createElement('div');
        markerElement.innerHTML = `
            <div class="${styles['custom-map-pin']}">
                <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 0C8 0 0 7 0 16C0 28 16 40 16 40C16 40 32 28 32 16C32 7 24 0 16 0Z" fill="black"/>
                </svg>
                <span class="${styles['custom-map-pin-number']}">
                    ${properties.number || ''}
                </span>
            </div>
        `;

        const mapboxMarker = new window.mapboxgl.Marker({
            element: markerElement,
            anchor: 'bottom'
        })
            .setLngLat([position.lng, position.lat])
            .addTo(map);

        currentMarkerRef.current = mapboxMarker;
    }
};

const MapboxDetailView: React.FC<MapboxDetailViewProps> = ({ stopNumber, address, longitude, latitude, markerProperties = {}, onCoordinatesReady, pageMarker }) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);

    // 地图加载打点
    useMapBoxPageLoadTrack(pageMarker);

    const [apiKey, setApiKey] = useState('');
    const [error, setError] = useState('');
    const [isError, setIsError] = useState(false);
    const mapRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const markerRef = useRef<any>(null);
    const updateTimerRef = useRef<NodeJS.Timeout>();
    const lastUpdateRef = useRef<{
        address: string;
        apiKey: string;
        stopNumber: number|String;
        markerProperties: any;
    } | null>(null);

    // 使用 useCallback 包装 updateLocation 函数
    const updateLocation = useCallback(async () => {
        if (!mapRef.current || !address || !apiKey) return;

        // 清除之前的定时器
        if (updateTimerRef.current) {
            clearTimeout(updateTimerRef.current);
        }

        // 设置新的定时器
        updateTimerRef.current = setTimeout(async () => {
            setIsLoading(true);
            setIsError(false);
            console.log('Updating location for address:', address);
            try {
                const position = await mapboxService.geocode(address, apiKey, longitude, latitude);
                console.log('Position result:', position);
                if (position) {
                    mapboxService.updateMarker(mapRef.current, {
                        position,
                        properties: {
                            id: `stop-${stopNumber}`,
                            number: stopNumber,
                            title: markerProperties.title || `Stop ${stopNumber}`,
                            ...markerProperties
                        }
                    }, markerRef);
                    // 只有在成功解析地址时才设置地图中心点
                    mapRef.current.setCenter([position.lng, position.lat]);
                    // 通知父组件坐标已准备好
                    if (onCoordinatesReady) {
                        onCoordinatesReady(position.lat, position.lng);
                    }
                } else {
                    setError(t('map.errors.locationNotFound') as string);
                    setIsError(true);
                }
            } catch (err) {
                console.error('Location update failed:', err);
                setError(t('map.errors.geocodeFailed') as string);
                setIsError(true);
            } finally {
                setIsLoading(false);
            }
        }, 5000);
    }, [address, apiKey, stopNumber, markerProperties, longitude, latitude, t]);

    // 初始化地图
    useEffect(() => {
        if (!apiKey || !containerRef.current) return;
        console.log('Initializing map with API key:', apiKey);

        const initMap = async () => {
            try {
                // 传入初始地址和经纬度，让地图初始化时就直接定位到正确位置
                mapRef.current = await mapboxService.initializeMap(containerRef.current!, apiKey, address, longitude, latitude);
                console.log('Map initialized successfully');

                // 如果有地址或有效的经纬度，添加标记
                if ((address && apiKey) || (longitude !== undefined && longitude !== null && 
                                            latitude !== undefined && latitude !== null)) {
                    const position = await mapboxService.geocode(address, apiKey, longitude, latitude);
                    if (position) {
                        mapboxService.updateMarker(mapRef.current, {
                            position,
                            properties: {
                                id: `stop-${stopNumber}`,
                                number: stopNumber,
                                title: markerProperties.title || `Stop ${stopNumber}`,
                                ...markerProperties
                            }
                        }, markerRef);
                        // 更新成功后，保存当前值，防止重复更新
                        lastUpdateRef.current = { address, apiKey, stopNumber, markerProperties };
                        // 通知父组件坐标已准备好
                        if (onCoordinatesReady) {
                            onCoordinatesReady(position.lat, position.lng);
                        }
                    } else {
                        // 添加错误处理
                        setError(t('map.errors.locationNotFound') as string);
                        setIsError(true);
                    }
                }
            } catch (err) {
                console.error('Map initialization failed:', err);
                setError(t('map.errors.initFailed') as string);
                setIsError(true);
            } finally {
                setIsLoading(false);
            }
        };

        initMap();

        return () => {
            if (markerRef.current) {
                markerRef.current.remove();
            }
            if (mapRef.current) {
                mapRef.current.remove();
            }
        };
    }, [apiKey, t]);

    // 处理地址更新
    useEffect(() => {
        const currentValues = { address, apiKey, stopNumber, markerProperties };
        const lastValues = lastUpdateRef.current;

        // 检查值是否真的改变了
        const hasChanged = !lastValues ||
            lastValues.address !== address ||
            lastValues.apiKey !== apiKey ||
            lastValues.stopNumber !== stopNumber ||
            JSON.stringify(lastValues.markerProperties) !== JSON.stringify(markerProperties);

        if (!hasChanged) {
            console.log('Values unchanged, skipping update');
            return;
        }

        // 如果是地图初始化后的第一次更新，跳过
        if (lastValues === null) {
            console.log('Skipping first update after initialization');
            return;
        }

        console.log('Location update effect triggered by:', {
            address,
            apiKey,
            stopNumber,
            markerProperties,
            hasMap: !!mapRef.current
        });

        if (!mapRef.current || !address || !apiKey) {
            console.log('Skipping location update:', {
                hasMap: !!mapRef.current,
                address,
                hasApiKey: !!apiKey
            });
            return;
        }

        // 直接在这里实现防抖逻辑，而不是调用 updateLocation
        if (updateTimerRef.current) {
            clearTimeout(updateTimerRef.current);
        }

        updateTimerRef.current = setTimeout(async () => {
            setIsLoading(true);
            setIsError(false);
            console.log('Updating location for address:', address);
            try {
                const position = await mapboxService.geocode(address, apiKey, longitude, latitude);
                console.log('Position result:', position);
                if (position) {
                    mapboxService.updateMarker(mapRef.current, {
                        position,
                        properties: {
                            id: `stop-${stopNumber}`,
                            number: stopNumber,
                            title: markerProperties.title || `Stop ${stopNumber}`,
                            ...markerProperties
                        }
                    }, markerRef);
                    mapRef.current.setCenter([position.lng, position.lat]);
                    // 更新成功后，保存当前值
                    lastUpdateRef.current = currentValues;
                } else {
                    setError(t('map.errors.locationNotFound') as string);
                    setIsError(true);
                }
            } catch (err) {
                console.error('Location update failed:', err);
                setError(t('map.errors.geocodeFailed') as string);
                setIsError(true);
            } finally {
                setIsLoading(false);
            }
        }, 5000);

        return () => {
            if (updateTimerRef.current) {
                clearTimeout(updateTimerRef.current);
            }
        };
    }, [address, apiKey, stopNumber, markerProperties, t]);

    // 获取 API 密钥
    useEffect(() => {

        const getApiKey = async () => {
            try {
                setApiKey(DEFAULT_MAPBOX_API_KEY);
                return;
            } catch (err) {
                console.error('Failed to set API key:', err);
                setError(t('map.errors.apiKeyFailed') as string);
            }
        };

        getApiKey();
    }, [t]);

    return (
        <div className="relative h-full bg-gray-100">
            {!isError && (
                <div
                    ref={containerRef}
                    className="w-full h-full"
                />
            )}
            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                    <div className="text-lg">{t('map.loading')}</div>
                </div>
            )}
            {isError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#f2f2f2] border border-gray-200 rounded-lg">
                    <div className="flex flex-col items-center space-y-4 px-6">
                        <div className="text-4xl mb-2">📍</div>
                        <div className="text-gray-600 text-lg font-medium text-center">
                            {t('map.errors.locationNotFound')}
                        </div>
                        <div className="text-gray-500 text-base text-center">
                            {t('map.errors.cannotLocate')}
                        </div>
                        <button
                            onClick={() => updateLocation()}
                            className="mt-4 px-6 py-2 bg-white border border-gray-300 rounded-full text-gray-600 text-sm font-medium hover:bg-gray-50 active:bg-gray-100 transition-colors duration-200"
                        >
                            {t('map.retry')}
                        </button>
                    </div>
                </div>
            )}
            {error && !isError && (
                <div className="absolute top-4 left-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            )}
        </div>
    );
};

export default MapboxDetailView;

